export const SUPPORT_BRANDS = ["dope", "montec", "ridestore"] as const;

export type SupportBrand = (typeof SUPPORT_BRANDS)[number];

export const WHATSAPP_SUPPORT_NUMBERS: Readonly<Record<SupportBrand, string>> = Object.freeze({
  dope: "+46767266653",
  montec: "+46731728861",
  ridestore: "+46767261976"
});

// Regions are append-only: persisted conversation keys (`brand:sms:region:phone`)
// retain their region token even when it is no longer configured, so removing
// or renaming one makes replies fail until those keys are migrated. Keys must
// be lower-case two-letter codes.
function smsRegionNumbers(
  numbers: Partial<Record<SupportBrand, string>>
): Readonly<Partial<Record<SupportBrand, string>>> {
  return Object.freeze(numbers);
}

export const SMS_SUPPORT_NUMBERS_BY_REGION = Object.freeze({
  ca: smsRegionNumbers({
    dope: "+14283087681",
    montec: "+12366095890"
  }),
  se: smsRegionNumbers({
    dope: "+46767266653",
    montec: "+46731728861",
    ridestore: "+46767261976"
  }),
  us: smsRegionNumbers({
    dope: "+17624758782",
    montec: "+19016658190"
  })
});

export type SmsSupportRegion = keyof typeof SMS_SUPPORT_NUMBERS_BY_REGION;

// Calling code per SMS region. Keyed on the region type, so provisioning a new
// region is a compile error until its code is listed here. `ca` and `us` share
// +1 (NANP): a +1 number cannot be attributed to one of them without area-code
// parsing, so a +1 customer is eligible when EITHER region has a sender for the
// brand — both would deliver.
const SMS_REGION_CALLING_CODE: Readonly<Record<SmsSupportRegion, string>> = Object.freeze({
  ca: "1",
  se: "46",
  us: "1"
});

// Pinned forever: legacy `brand:sms:phone` keys resolve to this region at read
// time, so changing it would silently re-point every legacy SMS conversation
// to another region's sender.
export const DEFAULT_SMS_SUPPORT_REGION: SmsSupportRegion = "se";

export const DEV_SUPPORT_NUMBER = "+14155238886";

const LANGUAGE_DEFAULT_REGION: Readonly<Record<string, SmsSupportRegion>> = Object.freeze({
  sv: DEFAULT_SMS_SUPPORT_REGION
});

// The market a storefront host serves when the URL/locale names none. Only
// non-base markets carry a path segment (/se, /ca, /eu), so a segment-less URL
// is the base market: Dope and Montec serve the US from their root (hreflang
// en-US + x-default), Ridestore the EU from its .com root (hreflang en-IE).
//
// Lives beside the number registry because both the widget stub and the worker
// need it to pick a market's SMS sender, and a second copy would drift.
// Ridestore also serves country domains, where the TLD is the market outright.
// They are listed here rather than derived from the TLD so an unknown host can
// never mint a market by suffix alone.
// A Map, not an object: an object lookup would also answer for inherited
// names such as "constructor" or "__proto__", minting a market for a non-host.
const STOREFRONT_BASE_MARKET: ReadonlyMap<string, string> = new Map([
  ["dopesnow.com", "us"],
  ["montecwear.com", "us"],
  ["ridestore.com", "eu"],
  ["ridestore.se", "se"],
  ["ridestore.de", "de"],
  ["ridestore.fi", "fi"],
  ["ridestore.no", "no"]
]);

// Strips exactly one leading "www.", so www.www.dopesnow.com stays unknown.
export function baseMarketForStorefrontHost(
  hostname: string | undefined | null
): string | null {
  const host = hostname?.trim().toLowerCase().replace(/^www\./, "");
  return host ? STOREFRONT_BASE_MARKET.get(host) ?? null : null;
}

export function normalizeSupportBrand(value: string | undefined | null): SupportBrand {
  const normalized = value?.trim().toLowerCase();
  if (normalized === "dope" || normalized === "dopesnow") return "dope";
  if (normalized === "montec") return "montec";
  return "ridestore";
}

export function supportRegionFromLocale(locale: string | undefined | null): string | null {
  const parts = locale?.trim().toLowerCase().replace(/_/g, "-").split("-").filter(Boolean);
  if (!parts?.length) return null;

  // BCP 47 extension / private-use sequences start with a singleton subtag;
  // nothing after one is a region ("sv-u-ca-gregory" must not read "ca").
  const subtags = parts.slice(1);
  const extensionStart = subtags.findIndex((part) => part.length === 1);
  const searchable = extensionStart === -1 ? subtags : subtags.slice(0, extensionStart);
  const explicitRegion = searchable.find((part) => /^[a-z]{2}$|^\d{3}$/.test(part));
  return explicitRegion ?? LANGUAGE_DEFAULT_REGION[parts[0]] ?? null;
}

export function isSmsSupportRegion(value: string): value is SmsSupportRegion {
  return value === value.toLowerCase()
    && Object.prototype.hasOwnProperty.call(SMS_SUPPORT_NUMBERS_BY_REGION, value);
}

export function getWhatsAppSupportNumber(brand: SupportBrand): string {
  return WHATSAPP_SUPPORT_NUMBERS[brand];
}

export function getSmsSupportNumber(
  region: string | undefined | null,
  brand: SupportBrand
): string | null {
  const normalizedRegion = region?.trim().toLowerCase();
  if (!normalizedRegion || !isSmsSupportRegion(normalizedRegion)) return null;
  return SMS_SUPPORT_NUMBERS_BY_REGION[normalizedRegion][brand] ?? null;
}

export function getSmsSupportNumberForLocale(
  locale: string | undefined | null,
  brand: SupportBrand
): string | null {
  return getSmsSupportNumber(supportRegionFromLocale(locale), brand);
}

function normalizePhoneNumber(value: string): string {
  return value.replace(/^whatsapp:/i, "").replace(/\s+/g, "").trim();
}

// Where two regions share a calling code, the sender we prefer. +1 is both `ca`
// and `us` and telling them apart needs area-code data we don't have; `us` wins,
// matching the storefront market resolver's NANP tie-break. Either sender
// reaches the customer, and because the chosen region is pinned into the
// conversation key, their reply lands back on the same thread either way.
const NANP_PREFERRED_REGION: SmsSupportRegion = "us";

/**
 * The region whose sender we would use to text this customer for this brand, or
 * null when the brand has no sender in the number's market.
 *
 * Takes an already-E.164 number; anything else is refused (fail closed) rather
 * than guessed at, since a national-format number's market is unknowable here.
 *
 * Callers starting a NEW conversation must feed the result into
 * `buildConversationKey`, because the key's region is what selects the `From`
 * number on every later send. Choosing a sender without recording it in the key
 * splits the thread: the customer replies to the number we texted from, and
 * inbound derives the region from that number, so a mismatched key opens a
 * second conversation.
 */
export function smsSupportRegionForPhone(
  phone: string | undefined | null,
  brand: SupportBrand
): SmsSupportRegion | null {
  const matches = smsSupportRegionsForPhone(phone, brand);
  if (!matches.length) return null;
  return matches.find((region) => region === NANP_PREFERRED_REGION) ?? matches[0]!;
}

/**
 * Every region whose sender could text this customer number for this brand:
 * more than one only where regions share a calling code (+1 is `us` and `ca`).
 * Lets a caller keep a region it already knows the customer uses (an existing
 * SMS thread) instead of the tie-break `smsSupportRegionForPhone` picks.
 */
export function smsSupportRegionsForPhone(
  phone: string | undefined | null,
  brand: SupportBrand
): SmsSupportRegion[] {
  const number = phone ? normalizePhoneNumber(phone) : "";
  if (!number.startsWith("+")) return [];
  return (Object.keys(SMS_SUPPORT_NUMBERS_BY_REGION) as SmsSupportRegion[]).filter(
    (region) =>
      number.startsWith(`+${SMS_REGION_CALLING_CODE[region]}`) &&
      SMS_SUPPORT_NUMBERS_BY_REGION[region][brand] != null
  );
}

/** What an operator is told when `hasSmsSupportForPhone` refuses their text. */
export const NO_SMS_NUMBER_FOR_CUSTOMER_REASON =
  "We have no SMS number for this customer's country, so we can't text them. Use another channel instead.";

/**
 * Whether we could deliver an SMS to this customer number for this brand —
 * true when the number's calling code belongs to a region where the brand has
 * a provisioned sender. Derived from the number registry rather than a separate
 * market allowlist, so provisioning a number is the only step needed to enable
 * a market.
 */
export function hasSmsSupportForPhone(
  phone: string | undefined | null,
  brand: SupportBrand
): boolean {
  return smsSupportRegionForPhone(phone, brand) != null;
}

export function getSupportBrandForNumber(
  value: string,
  channel: "whatsapp" | "sms"
): SupportBrand | null {
  const number = normalizePhoneNumber(value);
  if (channel === "whatsapp") {
    return SUPPORT_BRANDS.find((brand) => WHATSAPP_SUPPORT_NUMBERS[brand] === number) ?? null;
  }

  for (const region of Object.keys(SMS_SUPPORT_NUMBERS_BY_REGION) as SmsSupportRegion[]) {
    const brand = SUPPORT_BRANDS.find(
      (candidate) => SMS_SUPPORT_NUMBERS_BY_REGION[region][candidate] === number
    );
    if (brand) return brand;
  }
  return null;
}

export function getSmsSupportRegionForNumber(value: string): SmsSupportRegion | null {
  const number = normalizePhoneNumber(value);
  for (const region of Object.keys(SMS_SUPPORT_NUMBERS_BY_REGION) as SmsSupportRegion[]) {
    if (SUPPORT_BRANDS.some((brand) => SMS_SUPPORT_NUMBERS_BY_REGION[region][brand] === number)) {
      return region;
    }
  }
  return null;
}
